import * as turf from '@turf/turf';

import { lv95ToWgs84ForOneCoordinate, wgs84ToLv95ForOneCoordinate } from '../map/coordinateTransforms';
import type { Lv95Coordinate, TurfPolygon } from './cadastralShared';
import type { GeoAdminIdentifyFeature } from 'utils/geoAdminIdentifyHelper';
import { extractLargestLv95RingCoordinates } from 'utils/geometryHelper.js';

const buildTurfPolygonFromLv95 = (lv95Coordinates: Lv95Coordinate[] | undefined | null): TurfPolygon | null => {
  if (!lv95Coordinates?.length) {
    return null;
  }

  const ring = lv95Coordinates.map((coord) => {
    const point = lv95ToWgs84ForOneCoordinate(coord.x, coord.y);
    return [point.lng, point.lat];
  });

  return turf.polygon([[...ring, ring[0]]]);
};

export const buildLv95PolygonGeometry = (coordinates: Lv95Coordinate[]): GeoJSON.Polygon => {
  const ring = coordinates.map((coordinate) => [coordinate.x, coordinate.y]);
  const firstPoint = ring[0];
  const lastPoint = ring[ring.length - 1];
  const isClosed = firstPoint?.[0] === lastPoint?.[0] && firstPoint?.[1] === lastPoint?.[1];

  return {
    type: 'Polygon',
    coordinates: [isClosed ? ring : [...ring, firstPoint]],
  };
};

const convertWgs84GeometryToLv95 = (geometry: GeoJSON.Geometry): GeoJSON.Geometry => {
  const convertRing = (ring: number[][]) =>
    ring.map((coordinate) => {
      const point = wgs84ToLv95ForOneCoordinate(Number(coordinate?.[0]), Number(coordinate?.[1]));
      return [point.x, point.y];
    });

  if (geometry.type === 'Polygon') {
    return {
      ...geometry,
      coordinates: geometry.coordinates.map(convertRing),
    };
  }

  if (geometry.type === 'MultiPolygon') {
    return {
      ...geometry,
      coordinates: geometry.coordinates.map((polygon) => polygon.map(convertRing)),
    };
  }

  return geometry;
};

export const getParcelFeatureKey = (feature: GeoAdminIdentifyFeature) => {
  const keyCandidate = feature.featureId ?? feature.id ?? feature.properties?.featureId ?? feature.properties?.id;
  return keyCandidate != null ? String(keyCandidate) : JSON.stringify(feature.geometry);
};

export const getParcelFeaturePolygon = (feature: GeoAdminIdentifyFeature) => {
  const rawLv95Coordinates = extractLargestLv95RingCoordinates(feature?.geometry as GeoJSON.Geometry | null);
  const lv95Coordinates = rawLv95Coordinates;

  return {
    coordinates: lv95Coordinates,
    polygon: buildTurfPolygonFromLv95(lv95Coordinates),
  };
};

export const mergeSelectedParcelFeatures = (features: GeoAdminIdentifyFeature[]) => {
  const parcelPolygons = features
    .map((feature) => getParcelFeaturePolygon(feature).polygon)
    .filter((polygon): polygon is TurfPolygon => polygon != null);

  if (parcelPolygons.length !== features.length || parcelPolygons.length === 0) {
    return {
      feature: null,
      error: 'Unable to use one or more cadastral parcels.',
    };
  }

  if (parcelPolygons.length === 1) {
    return {
      feature: features[0] as unknown as GeoJSON.Feature,
      error: null,
    };
  }

  const mergedPolygon = turf.union(turf.featureCollection(parcelPolygons));
  if (!mergedPolygon) {
    return {
      feature: null,
      error: 'Unable to merge selected cadastral parcels.',
    };
  }

  const flattened = turf.flatten(turf.featureCollection([mergedPolygon]));
  if (flattened.features.length !== 1) {
    return {
      feature: null,
      error: 'Only adjacent parcels can be saved together.',
    };
  }

  return {
    feature: {
      ...flattened.features[0],
      geometry: convertWgs84GeometryToLv95(flattened.features[0].geometry),
    } as GeoJSON.Feature,
    error: null,
  };
};

export const mergeSelectedParcelFeatureGroups = (features: GeoAdminIdentifyFeature[]) => {
  const polygons = features.map((feature) => getParcelFeaturePolygon(feature).polygon);
  if (polygons.some((polygon) => polygon == null)) {
    return { features: [], error: 'Unable to use one or more cadastral parcels.' };
  }

  const remaining = new Set(features.map((_, index) => index));
  const groups: GeoAdminIdentifyFeature[][] = [];
  while (remaining.size > 0) {
    const pending = [remaining.values().next().value as number];
    const group: GeoAdminIdentifyFeature[] = [];
    remaining.delete(pending[0]);

    while (pending.length > 0) {
      const index = pending.pop() as number;
      group.push(features[index]);
      for (const candidateIndex of remaining) {
        if (turf.booleanIntersects(polygons[index] as TurfPolygon, polygons[candidateIndex] as TurfPolygon)) {
          remaining.delete(candidateIndex);
          pending.push(candidateIndex);
        }
      }
    }
    groups.push(group);
  }

  const mergedGroups = groups.map(mergeSelectedParcelFeatures);
  const failedGroup = mergedGroups.find(({ feature }) => !feature);
  return failedGroup
    ? { features: [], error: failedGroup.error }
    : { features: mergedGroups.map(({ feature }) => feature as GeoJSON.Feature), error: null };
};
